import {PublisherOrSiteOption,PageState,ProductRecord,CampaignRecord,PlacementRecord,CLinkOwnerOption, SortState, ColumnIds, IdName, DateModes, QueryFlags} from "../utils/types";
import StateHelper from "../utils/StateHelper";
import {PagesHelper} from "./PagesHelper";
import { DatagridHelper } from "../datagrid/DatagridHelper";
import dayjs from '../utils/dayjs';

export function isDateGroupBy(id : string) : boolean {
  return id === ColumnIds.date || id === ColumnIds.week || id === ColumnIds.month;
}

/**
 * Ensure only a single date period group by (date, week, month) is selected at a time,
 * giving preference to the new one selected.
 * 
 * Takes the new selection (from the type ahead box), the existing selection (from the pageState)
 * and returns the new selection, but with any extra date period groupBys removed. 
 * 
 * If more than 2 date groupBys are somehow selected, then the first one is kept and the others are removed.
 */
export function toggleDateGroupBys(newSelection: IdName[], existingSelection: string): IdName[] {
  const existingDateGroupBys = (existingSelection || "").split(",").filter( gb => isDateGroupBy(gb) );

  // figure out what date groupBys are being selected, and which ones weren't previously selected
  const dateGroupBys = newSelection.filter( val => isDateGroupBy(val.id) ).map( val => val.id );
  const newDateGroupBys = dateGroupBys.filter( val => existingDateGroupBys.indexOf(val) === -1);

  // no date group bys - nothing to change
  if ( dateGroupBys.length === 0 ) {
    return newSelection;
  }

  // if any of the date groupBys are new, return that one (plus all non-date groupBys)
  if ( newDateGroupBys.length >= 1 ) {
    // only keep the first newGroupBy value and remove the other dateGroupBys
    return newSelection.filter( val => !isDateGroupBy(val.id) || val.id === newDateGroupBys[0] );
  }

  // otherwise, all of the date groupBys were already selected, so just keep the first one
  return newSelection.filter( val => !isDateGroupBy(val.id) || val.id === dateGroupBys[0] );
}

export const FiltersEventsHelper = {
  groupByOnChange : function(page :string, pageState : PageState, sortState:SortState,  selectedValues: IdName[]) {

    selectedValues = toggleDateGroupBys(selectedValues, pageState.groupBy);

    if (selectedValues && selectedValues.length > 0) {
      if (selectedValues.find((column) => column.id === ColumnIds.date) && !pageState.groupBy.includes(ColumnIds.date)) {
        sortState.columnIdSorted = ColumnIds.date;
        sortState.sort = 1;
      } else if (selectedValues.find((column) => column.id === ColumnIds.week) && !pageState.groupBy.includes(ColumnIds.week)) {
        sortState.columnIdSorted = ColumnIds.week;
        sortState.sort = 1;
      } else if (selectedValues.find((column) => column.id === ColumnIds.month) && !pageState.groupBy.includes(ColumnIds.month)) {
        sortState.columnIdSorted = ColumnIds.month;
        sortState.sort = 1;
      }


      let groupBy = "";
      for (let i = 0; i < selectedValues.length; i++) {
        if( i > 0 ){
          groupBy += ",";
        }
        const element = selectedValues[i];
        groupBy += element.id;
      }
      pageState.groupBy = groupBy;
    } else {
      pageState.groupBy = null;
    }    
    if (pageState.pivotBy !== null) {
      const isOnPivotSelection = false;
      const { newGroupBy } = DatagridHelper.checkPivotCombinationWithGroupsByAndDates(pageState.pivotBy, pageState.groupBy, pageState.dateStart, pageState.dateEnd, isOnPivotSelection, pageState.dateMode);
      pageState.groupBy = newGroupBy;
    }

    const searchFuntion = StateHelper.getSearchFuntion(page);
    searchFuntion();
  },

  trafficTypeOnChange : function(page :string, pageState : PageState, selectedValues: any[]){
    if (selectedValues && selectedValues.length > 0) {
      let trafficTypes = "";
      for (let i = 0; i < selectedValues.length; i++) {
        if( i > 0 ){
          trafficTypes += ",";
        }
        
        const element = selectedValues[i];
        
        switch(element.id){
          case "all-mobile":
            trafficTypes += "mobile-android,mobile-ios,mobile-other";
            break;
          case "mobile-android":
            trafficTypes += "mobile-android";
            break;
          case "mobile-ios":
            trafficTypes += "mobile-ios";
            break;
          case "mobile-other":
            trafficTypes += "mobile-other";
            break;
          case "all-desktop":
            trafficTypes += "desktop-other,desktop-NoUserAgent";
            break;
          case "desktop-other":
            trafficTypes += "desktop-other";
            break;
          case "desktop-NoUserAgent":
            trafficTypes += "desktop-NoUserAgent";
            break;
        }
      }
      pageState.trafficTypes = trafficTypes;
    } else {
      pageState.trafficTypes = null;
    }    

    const searchFuntion = StateHelper.getSearchFuntion(page);
    searchFuntion();
  },

  dateRangeOnChange : function(page :string,pageState : PageState, sortState:SortState, start: dayjs.Dayjs, end: dayjs.Dayjs, dateMode: DateModes){
    pageState.dateStart = start;
    pageState.dateEnd = end;
    const isOnPivotSelection = false;
    const { newGroupBy, newDateStart, newDateEnd, newPivotBy, newDateMode } = DatagridHelper.checkPivotCombinationWithGroupsByAndDates(pageState.pivotBy, pageState.groupBy, pageState.dateStart, pageState.dateEnd, isOnPivotSelection, dateMode);
 
    if (newGroupBy.includes(ColumnIds.date) && !pageState.groupBy.includes(ColumnIds.date)) {
      sortState.columnIdSorted = ColumnIds.date;
      sortState.sort = 1;
    } else if (newGroupBy.includes(ColumnIds.week) && !pageState.groupBy.includes(ColumnIds.week)) {
      sortState.columnIdSorted = ColumnIds.week;
      sortState.sort = 1;
    } else if (newGroupBy.includes(ColumnIds.month) && !pageState.groupBy.includes(ColumnIds.month)) {
      sortState.columnIdSorted = ColumnIds.month;
      sortState.sort = 1;
    }

    pageState.dateMode = newDateMode;
    pageState.dateStart = newDateStart;
    pageState.dateEnd = newDateEnd;
    pageState.pivotBy = newPivotBy;
    pageState.groupBy = newGroupBy;

    const searchFuntion = StateHelper.getSearchFuntion(page);
    searchFuntion();    
  },

  countriesDropdownOnChange : function (page : string, pageState : PageState, selectedValues: any[]){
    if (selectedValues && selectedValues.length > 0) {
      let countries = "";
      for (let i = 0; i < selectedValues.length; i++) {
        if( i > 0 ){
          countries += ",";
        }
        const element = selectedValues[i];
        countries += element.id;
      }
      pageState.countries = countries;
    } else {
      pageState.countries = null;
    }

    const searchFuntion = StateHelper.getSearchFuntion(page);
    searchFuntion();
  },
    
  trafficSourcesDropDownOnChange : function(page : string, pageState : PageState, selectedValues: PublisherOrSiteOption[]){
    const selectedTrafficSources:string[] = [];
    selectedValues.forEach((selectedValue:PublisherOrSiteOption) => {
      const index = selectedTrafficSources.indexOf(selectedValue.key);
      if (index !== -1) {
        selectedTrafficSources.splice(index, 1);
      } else {
        selectedTrafficSources.push(selectedValue.key);
      }
    });

    pageState.publishers = selectedTrafficSources;
    const searchFuntion = StateHelper.getSearchFuntion(page);

    searchFuntion();
  },
  queryFlagsDropDownOnChange : function(page : string, pageState : PageState, selectedValues: QueryFlags[]){
    const selectedQueryFlags:QueryFlags[] = [];
    selectedValues.forEach((selectedValue:QueryFlags) => {
      const index = selectedQueryFlags.indexOf(selectedValue);
      if (index !== -1) {
        selectedQueryFlags.splice(index, 1);
      } else {
        selectedQueryFlags.push(selectedValue);
      }
    });

    pageState.queryFlags = selectedQueryFlags;
    const searchFuntion = StateHelper.getSearchFuntion(page);

    searchFuntion();
  },

  placementsDropDownOnChange : function(page : string, pageState : PageState, selectedValues: PlacementRecord[]) : void {

    if (selectedValues && selectedValues.length > 0) {

   
      //BEGIN check repeated values
      const lastElement : PlacementRecord = selectedValues[selectedValues.length - 1];

      lastElement.apptapPublisherId = lastElement.apptapPublisherId === null ? "***" : lastElement.apptapPublisherId;
      lastElement.apptapSiteId = lastElement.apptapSiteId === null ? "***" : lastElement.apptapSiteId;
      lastElement.publisherPlacementId = lastElement.publisherPlacementId === null ? "***" : lastElement.publisherPlacementId;

      const tmp : PlacementRecord[] = selectedValues.filter((e : PlacementRecord) => {
        return e.apptapPublisherId === lastElement.apptapPublisherId && 
                e.apptapSiteId === lastElement.apptapSiteId && 
                e.publisherPlacementId === lastElement.publisherPlacementId;
      });

      if ( tmp.length > 1){
        //the last element is repeated, let's drop it from the array.
        selectedValues.pop();
        return;
      }
      //END check repeated values

      const selectedPlacement : string[]= [];
      for (let i = 0; i < selectedValues.length; i++) {
        const element = selectedValues[i];
        let fullPlacementValue = (element.apptapPublisherId||"***") + ":" +  (element.apptapSiteId||"***") + ":" + element.publisherPlacementId;
        selectedPlacement.push(fullPlacementValue);
      }
      pageState.placements = selectedPlacement;
    } else {
      pageState.placements = [];
    }

    const searchFuntion = StateHelper.getSearchFuntion(page);
    searchFuntion();
  },

  productsDropdownOnChange : function (page : string, pageState : PageState, selectedValues: ProductRecord[]){
    if (selectedValues && selectedValues.length > 0) {
      //BEGIN check repeated values
      const lastElement : ProductRecord = selectedValues[selectedValues.length - 1];
      const tmp : ProductRecord[] = selectedValues.filter((e : ProductRecord) => e.productId === lastElement.productId);

      if ( tmp.length > 1){
        //the last element is repeated, let's drop it from the array.
        selectedValues.pop();
        return;
      }
      //END check repeated values     
      pageState.productIds = (selectedValues as ProductRecord[]).map( p => p.productId ).join(",");
    } else {
      pageState.productIds = null;
    }

    const searchFuntion = StateHelper.getSearchFuntion(page);
    searchFuntion();
  },    

  campaignsDropdownOnChange : function (page : string, pageState : PageState, selectedValues: CampaignRecord[]){
    if (selectedValues && selectedValues.length > 0) {
      //BEGIN check repeated values
      const lastElement : CampaignRecord = selectedValues[selectedValues.length - 1];
      const tmp : CampaignRecord[] = selectedValues.filter((e : CampaignRecord) => e.campaignId === lastElement.campaignId && e.campaignSourceAlias === lastElement.campaignSourceAlias);
      if ( tmp.length > 1){
        //the last element is repeated, let's drop it from the array.
        selectedValues.pop();
        return;
      }
      //END check repeated values 

      const campaigns = (selectedValues as CampaignRecord[]).map( campaign => {
        if (campaign.customOption) {
          campaign.campaignSourceAlias = campaign.labelKey.includes(":") ? campaign.labelKey.split(":")[0] : campaign.labelKey;
          campaign.campaignId = campaign.labelKey.includes(":") ? campaign.labelKey.split(":")[1] : "***";
        }
        return PagesHelper.getCampaignSourceAliasAndID(campaign);
      });

      pageState.campaigns = campaigns;
    } else {
      pageState.campaigns = null;
    }   
    const searchFuntion = StateHelper.getSearchFuntion(page);
    searchFuntion();
  },

  cLinksDropdownOnChange : function (page : string, pageState : PageState, selectedValues: CLinkOwnerOption[]) {
    if (selectedValues && selectedValues.length > 0) {

      const lastElement : CLinkOwnerOption = selectedValues[selectedValues.length - 1];
      const tmp : CLinkOwnerOption[] = selectedValues.filter((e : CLinkOwnerOption) => e.id === lastElement.id);

      if ( tmp.length > 1){
        const owner = selectedValues.find((e : CLinkOwnerOption) => e.id === lastElement.id);
        if (owner) {
          selectedValues.splice(selectedValues.indexOf(owner), 1);
        }
      }

      const cLinks = (selectedValues as CLinkOwnerOption[]).map( c => {
        return PagesHelper.fromCLinkOwnerOptionToString(c);
      });

      pageState.cLinks = cLinks;
    } else {
      pageState.cLinks = null;
    }   
    const searchFuntion = StateHelper.getSearchFuntion(page);
    searchFuntion();
  },
};
